import serverName from "Config/config";

/**
 * Break-glass emergency access.
 *
 * These endpoints do NOT go through WrapperAPI. WrapperAPI toasts and throws on
 * any status >= 400, but several statuses here are normal parts of the flow and
 * need to render inline: 400 is a wrong authenticator code (with
 * `attempts_remaining`), 429 is a lockout (with `retry_after`), 409 asks for
 * re-enrollment. Letting the generic wrapper swallow those would replace a
 * useful message with "Server Error Occurred".
 */

// Under /api/authentication/ deliberately: that prefix is the one place nginx
// proxies WITHOUT the management auth cookie (setup/nginx_docker and
// backend-bff/nginx.conf both gate every other /api/ path on it). Anywhere else
// the person break-glass exists for - an admin who cannot log in - gets a 401
// from nginx before Django sees the request.
const BASE = `${serverName}api/authentication/break-glass`;

/**
 * Never rejects.
 *
 * Every caller here is a step in a login flow with its own spinner and disabled
 * button. If a dropped connection escaped as a rejection, the Send button would
 * stay disabled, the landing page would spin forever, and the code form would
 * sit in `loading` with nothing to click — an admin locked out of the escape
 * hatch by a flaky network. So a transport failure is normalised into the same
 * shape as a server answer, with httpStatus 0.
 */
async function post(path, body) {
    let response;
    try {
        response = await fetch(`${BASE}/${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body ?? {}),
            credentials: "include",
        });
    } catch (error) {
        return {
            status: false,
            httpStatus: 0,
            info: "Could not reach the server. Check your connection and try again.",
        };
    }

    let payload = {};
    try {
        payload = await response.json();
    } catch (error) {
        payload = {};
    }

    return { ...payload, httpStatus: response.status };
}

/**
 * Ask for an emergency sign-in link.
 *
 * Always resolves to the same body whatever the address is — an emergency
 * admin, an ordinary user or nobody. There is deliberately nothing to branch on:
 * the emergency account is the one credential in a tenant that bypasses the IdP,
 * and a response that identified it would hand an attacker their target.
 */
const requestBreakGlassLink = async (email) =>
    post("request-link", { email });

/** Consume the emailed link. Returns `{ challenge, next, email, expires_in }`. */
const redeemBreakGlassLink = async (token) => post("redeem", { token });

/** First emergency sign-in only. Returns the authenticator key exactly once. */
const startBreakGlassTotp = async (challenge) =>
    post("totp/start", { challenge });

/** Confirms enrollment AND signs in — the session cookie is set on this response. */
const confirmBreakGlassTotp = async (challenge, code) =>
    post("totp/confirm", { challenge, code });

/** Verifies an authenticator code AND signs in, for a returning emergency admin. */
const verifyBreakGlassTotp = async (challenge, code) =>
    post("verify", { challenge, code });

export {
    requestBreakGlassLink,
    redeemBreakGlassLink,
    startBreakGlassTotp,
    confirmBreakGlassTotp,
    verifyBreakGlassTotp,
};
